import React, { useMemo } from 'react';
import sanitizer from '@helpers/sanitizer';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { usePreferences } from '@context/preferences.context';
import { ImagesService } from '@services/image-service';
import useInitialRequestUrlQuery from '@hooks/use-initial-request-url-query';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { ElasticSearchProduct } from '@interfaces/api/responses/elasticSearch';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { getGoogleShoppingCondition } from './schema-utils';

const MAX_NUMBER_OF_ITEMS = 10000;

const SIZE_SYSTEM_SCHEMA_URL_BY_STANDARD: Record<string, string> = {
  DE: 'https://schema.org/WearableSizeSystemDE',
  EU: 'https://schema.org/WearableSizeSystemEurope',
  FR: 'https://schema.org/WearableSizeSystemFR',
  IT: 'https://schema.org/WearableSizeSystemIT',
  UK: 'https://schema.org/WearableSizeSystemUK',
  US: 'https://schema.org/WearableSizeSystemUS',
};

const getSizeSpecification = (size?: CatalogProduct['size']) => {
  const name = size?.size;
  if (!name) {
    return undefined;
  }

  const sizeSystemLabel = size.standard ?? size.localizedStandard;
  const sizeSystem = sizeSystemLabel
    ? SIZE_SYSTEM_SCHEMA_URL_BY_STANDARD[sizeSystemLabel] ?? sizeSystemLabel
    : undefined;

  return {
    '@type': 'SizeSpecification',
    name,
    ...(sizeSystem && { sizeSystem }),
  };
};

const CatalogSchema: React.FC = () => {
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const totalResults = useContextSelector(CatalogContext, (v) => v.totalResults);
  const { initialRequestUrl } = useInitialRequestUrlQuery();
  const { locale } = usePreferences();

  const schema = useMemo(() => {
    if (!products?.length || !queryResult?.items) {
      return null;
    }

    const origin = getSeoBaseUrlFromLocale(locale);

    const searchItemsById = new Map<string, ElasticSearchProduct>();
    queryResult.items.forEach((item: ElasticSearchProduct) => {
      searchItemsById.set(String(item.id), item);
    });

    const itemListElement = products.map((product: CatalogProduct, index: number) => {
      const productUrl = origin ? `${origin}${product.path}` : product.path;
      const searchItem = searchItemsById.get(product.id);
      const conditionId = searchItem?.condition?.id;
      const priceCents = product.price?.cents ?? 0;
      const currency = product.price?.currency ?? '';
      const isUnavailable = product.sold || product.shouldBeGone;
      const color = product.colors?.all
        ?.map((colorOption) => colorOption.name)
        .filter(Boolean)
        .join(', ');
      const size = getSizeSpecification(product.size);

      const offer: Record<string, unknown> = {
        '@type': 'Offer',
        price: (priceCents / 100).toString(),
        priceCurrency: currency,
        availability: isUnavailable ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        ...(conditionId != null && { itemCondition: getGoogleShoppingCondition(conditionId) }),
        url: productUrl,
      };

      const regularCents = product.regularPrice?.cents;
      if (regularCents && regularCents > priceCents) {
        offer.priceSpecification = {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/StrikethroughPrice',
          price: (regularCents / 100).toString(),
          priceCurrency: currency,
        };
      }

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          '@id': productUrl,
          productID: product.id,
          name: product.name,
          url: productUrl,
          ...(color && { color }),
          ...(searchItem?.description && { description: searchItem.description }),
          ...(size && { size }),
          image: ImagesService.buildUrl(product.pictures?.[0]?.path, { w: 1024, h: 1024 }, true),
          brand: {
            '@type': 'Brand',
            name: product.brand?.name,
          },
          offers: offer,
        },
      };
    });

    const pageUrl =
      origin && initialRequestUrl ? `${origin}${initialRequestUrl.pathname}${initialRequestUrl.search}` : undefined;

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      ...(totalResults != null && { numberOfItems: Math.min(totalResults, MAX_NUMBER_OF_ITEMS) }),
      ...(pageUrl && { url: pageUrl }),
      itemListElement,
    };
  }, [products, queryResult, totalResults, initialRequestUrl, locale]);

  if (!schema) {
    return null;
  }

  return (
    <script
      data-cy="catalog-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: sanitizer(JSON.stringify(schema)) }}
    />
  );
};

export default CatalogSchema;
